/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { ArrowLeftOutlined, ArrowRightOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { Drawer, Form } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ConfigRevision, Node, NodeConfig, OsType } from '@/client';
import { useMessage } from '@/hooks/message';
import { useCreateNodeGroupMutation } from '@/hooks/mutation/useCreateNodeGroupMutation';
import { usePaginatedConfigRevisionsQuery } from '@/hooks/query/usePaginatedConfigRevisionsQuery';
import { usePaginatedNodeConfigsQuery } from '@/hooks/query/usePaginatedNodeConfigsQuery';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { ReviewStep } from '@/pages/FleetManagement/NewFleetWizardSteps/ReviewStep';
import { SelectConfigStep } from '@/pages/FleetManagement/NewFleetWizardSteps/SelectConfigStep';
import { SelectRevisionStep } from '@/pages/FleetManagement/NewFleetWizardSteps/SelectRevisionStep';
import { NewFleetFormFields } from '@/pages/FleetManagement/NewFleetWizardSteps/types';
import { Button } from '@/primitives/Button';

import './CreateFleetFromNodesWizard.css';
import { FleetDetailsStep } from './steps/FleetDetailsStep';
import { ReviewSelectedNodesStep } from './steps/ReviewSelectedNodesStep';

type WizardStep = 'reviewNodes' | 'selectConfig' | 'selectRevision' | 'details' | 'review';

export type CreateFleetFromNodesWizardProps = {
  open: boolean;
  onClose: () => void;
  initialNodes: Node[];
  tenantId: string;
};

export function CreateFleetFromNodesWizard({ open, onClose, initialNodes, tenantId }: CreateFleetFromNodesWizardProps) {
  const [form] = Form.useForm<NewFleetFormFields>();
  const [message] = useMessage();
  const { t } = useMimicTranslation('nodes');
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState<WizardStep>('reviewNodes');
  const [selectedNodes, setSelectedNodes] = useState<Node[]>(initialNodes);
  const [selectedConfig, setSelectedConfig] = useState<NodeConfig | undefined>();
  const [selectedRevision, setSelectedRevision] = useState<ConfigRevision | undefined>();
  const [formValues, setFormValues] = useState<NewFleetFormFields | undefined>();

  const operatingSystem = selectedNodes[0]?.operatingSystem;
  const subtenantId = selectedNodes[0]?.subtenant?.id || 'null';

  const nodesAlreadyInFleet = useMemo(() => {
    return new Set(selectedNodes.filter((n) => n.nodeGroup).map((n) => n.id));
  }, [selectedNodes]);

  const validNodes = useMemo(() => {
    return selectedNodes.filter((n) => !nodesAlreadyInFleet.has(n.id));
  }, [selectedNodes, nodesAlreadyInFleet]);

  const configsQuery = usePaginatedNodeConfigsQuery({
    params: {
      filters: { os: operatingSystem as OsType, subtenantId },
    },
    type: 'inMemory',
  });
  const updateConfigsQueryParams = configsQuery.updateQueryParams;

  const revisionsQuery = usePaginatedConfigRevisionsQuery({
    params: {
      configId: selectedConfig?.id || '',
    },
    type: 'inMemory',
  });

  useEffect(() => {
    if (open) {
      setSelectedNodes(initialNodes);
      setCurrentStep('reviewNodes');
      setSelectedConfig(undefined);
      setSelectedRevision(undefined);
      setFormValues(undefined);
      form.resetFields();
    }
  }, [open, initialNodes, form]);

  useEffect(() => {
    setSelectedRevision(undefined);
  }, [selectedConfig?.id]);

  useEffect(() => {
    if (operatingSystem) {
      updateConfigsQueryParams({ filters: { os: operatingSystem, subtenantId } });
    }
  }, [operatingSystem, subtenantId, updateConfigsQueryParams]);

  const { mutate: createNodeGroup, isPending } = useCreateNodeGroupMutation({
    onSuccess: (data) => {
      message.success(t('createFleetWizard.feedback.success'));
      handleClose();
      navigate(`/tenants/${tenantId}/fleet-management/${data.data.id}`);
    },
    onError: () => {
      message.error(t('createFleetWizard.feedback.error'));
    },
  });

  const handleClose = () => {
    form.resetFields();
    setCurrentStep('reviewNodes');
    setSelectedNodes([]);
    setSelectedConfig(undefined);
    setSelectedRevision(undefined);
    setFormValues(undefined);
    onClose();
  };

  const handleNext = async () => {
    if (currentStep === 'reviewNodes') {
      setCurrentStep('selectConfig');
    } else if (currentStep === 'selectConfig') {
      setCurrentStep('selectRevision');
    } else if (currentStep === 'selectRevision') {
      setCurrentStep('details');
    } else if (currentStep === 'details') {
      try {
        await form.validateFields();
        const values = form.getFieldsValue();
        setFormValues({ ...values, operatingSystem: operatingSystem as 'windows' | 'linux' });
        setCurrentStep('review');
      } catch {
        // Form validation failed
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep === 'selectConfig') {
      setCurrentStep('reviewNodes');
    } else if (currentStep === 'selectRevision') {
      setCurrentStep('selectConfig');
    } else if (currentStep === 'details') {
      setCurrentStep('selectRevision');
    } else if (currentStep === 'review') {
      setCurrentStep('details');
    }
  };

  const handleSubmit = async () => {
    if (!selectedConfig || !selectedRevision || !formValues || !operatingSystem) return;

    const nodeIds = validNodes.map((n) => n.id);

    createNodeGroup({
      name: formValues.name,
      description: formValues.description,
      operatingSystem: operatingSystem as OsType,
      configId: selectedConfig.id,
      configRevisionId: selectedRevision.id,
      nodeIds: nodeIds.length > 0 ? nodeIds : undefined,
    });
  };

  const handleRemoveNodes = (nodeIdsToRemove: string[]) => {
    setSelectedNodes((prev) => prev.filter((n) => !nodeIdsToRemove.includes(n.id)));
  };

  const getNodeConfigPath = (nodeConfig: NodeConfig) => `/tenants/${tenantId}/node-configs/${nodeConfig.id}`;
  const getNodeConfigRevisionPath = (revision: ConfigRevision) =>
    `/tenants/${tenantId}/node-configs/${revision.nodeConfigId}/revisions/${revision.revisionNumber}`;
  const getNodeConfigurationPath = (nodeConfigId: string) => `/tenants/${tenantId}/node-configs/${nodeConfigId}`;

  const hasNodesInFleet = selectedNodes.some((n) => nodesAlreadyInFleet.has(n.id));

  const canProceed: Record<WizardStep, boolean> = {
    reviewNodes: selectedNodes.length > 0 && !hasNodesInFleet,
    selectConfig: !!selectedConfig,
    selectRevision: !!selectedRevision,
    details: true,
    review: true,
  };

  const stepTitles: Record<WizardStep, string> = {
    reviewNodes: t('createFleetWizard.steps.reviewNodes'),
    selectConfig: t('createFleetWizard.steps.selectConfig'),
    selectRevision: t('createFleetWizard.steps.selectRevision'),
    details: t('createFleetWizard.steps.details'),
    review: t('createFleetWizard.steps.review'),
  };

  const footer = (
    <div className="create-fleet-wizard-footer">
      <div className="left-buttons">
        {currentStep !== 'reviewNodes' && (
          <Button dataTestId="previous-step" type="default" onClick={handlePrevious} disabled={isPending}>
            <ArrowLeftOutlined />
            {t('createFleetWizard.previous')}
          </Button>
        )}
      </div>
      <div className="right-buttons">
        <Button dataTestId="cancel-create-fleet" type="link" onClick={handleClose} disabled={isPending}>
          {t('createFleetWizard.cancel')}
        </Button>
        {currentStep !== 'review' && (
          <Button dataTestId="next-step" type="primary" onClick={handleNext} disabled={!canProceed[currentStep]}>
            {t('createFleetWizard.next')}
            <ArrowRightOutlined />
          </Button>
        )}
        {currentStep === 'review' && (
          <Button
            dataTestId="create-fleet-submit"
            type="primary"
            onClick={handleSubmit}
            disabled={isPending}
            loading={isPending}
          >
            {t('createFleetWizard.createFleet')}
            <CheckOutlined />
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <Drawer
      rootClassName="create-fleet-wizard"
      placement="top"
      title={stepTitles[currentStep]}
      open={open}
      height="100vh"
      onClose={handleClose}
      closeIcon={null}
      extra={<CloseButton onClick={handleClose} />}
      footer={footer}
    >
      {currentStep === 'reviewNodes' && (
        <ReviewSelectedNodesStep
          selectedNodes={selectedNodes}
          nodesInFleet={nodesAlreadyInFleet}
          onRemoveNodes={handleRemoveNodes}
          isLoading={false}
        />
      )}

      {currentStep === 'selectConfig' && (
        <SelectConfigStep
          configsQuery={configsQuery}
          selectedConfig={selectedConfig}
          onConfigSelect={setSelectedConfig}
          getNodeConfigPath={getNodeConfigPath}
          getNodeConfigRevisionPath={getNodeConfigRevisionPath}
        />
      )}

      {currentStep === 'selectRevision' && selectedConfig && (
        <SelectRevisionStep
          revisionsQuery={revisionsQuery}
          selectedRevision={selectedRevision}
          onRevisionSelect={setSelectedRevision}
          getConfigRevisionPath={getNodeConfigRevisionPath}
        />
      )}

      {currentStep === 'details' && operatingSystem && (
        <FleetDetailsStep form={form} operatingSystem={operatingSystem} isPending={isPending} />
      )}

      {currentStep === 'review' && formValues && selectedConfig && selectedRevision && (
        <ReviewStep
          tenantId={tenantId}
          formValues={formValues}
          selectedConfig={selectedConfig}
          selectedRevision={selectedRevision}
          selectedNodes={validNodes}
          getNodeConfigurationPath={getNodeConfigurationPath}
        />
      )}
    </Drawer>
  );
}

function CloseButton({ onClick }: { onClick: () => void }) {
  return (
    <Button dataTestId="close-create-fleet-wizard" type="text" onClick={onClick}>
      <CloseOutlined />
    </Button>
  );
}
